import {
  queryOptions,
  skipToken,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useMemo } from 'react'

import { commercialsApi } from '../../data/api/commercials.api'
import type { ListParams } from '../../data/types/ListParams'
import type { ActivityAction } from '../enums/ActivityAction'
import { GENERATION_STATUSES, type GenerationStatus } from '../enums/GenerationStatus'
import type { Commercial, CommercialUpsert } from '../models/Commercial'

export const commercialsKeys = {
  all: ['commercials'] as const,
  list: (params: ListParams) => ['commercials', params] as const,
  count: (filter?: Record<string, unknown>) => ['commercials', 'count', { filter }] as const,
  detail: (id: string | undefined) => ['commercials', 'detail', id] as const,
  status: (id: string, action?: ActivityAction) => ['commercials', 'status', id, action] as const,
  polling: (id: string | undefined) => ['commercials', 'polling', id] as const,
}

export function useCommercials(params: ListParams) {
  return useQuery({
    queryKey: commercialsKeys.list(params),
    queryFn: () => commercialsApi.list(params.skip, params.top, params.filter).then((r) => r.data),
  })
}

export function useCommercialsCount(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: commercialsKeys.count(filter),
    queryFn: () => commercialsApi.count(filter).then((r) => r.data),
  })
}

export function useCommercial(id: string | undefined) {
  return useQuery({
    queryKey: commercialsKeys.detail(id),
    queryFn: id ? () => commercialsApi.detail(id).then((r) => r.data) : skipToken,
  })
}

export function useCommercialCreate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CommercialUpsert) => commercialsApi.create(data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialsKeys.all }),
  })
}

export function useCommercialUpdate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CommercialUpsert }) =>
      commercialsApi.update(id, data).then((r) => r.data),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: commercialsKeys.all })
      qc.invalidateQueries({ queryKey: commercialsKeys.detail(id) })
    },
  })
}

export function useCommercialDelete() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => commercialsApi.delete(id).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: commercialsKeys.all }),
  })
}

export function useCommercialStatus(
  commercialId: string | undefined,
  action?: ActivityAction,
  enabled = false,
) {
  return useQuery({
    queryKey: commercialsKeys.status(commercialId ?? '', action),
    queryFn:
      commercialId && enabled
        ? () => commercialsApi.status(commercialId, action).then((r) => r.data.status)
        : skipToken,
    refetchInterval: (query) =>
      GENERATION_STATUSES.some((value) => value === query.state.data) ? 5000 : false,
  })
}

function commercialStatusOptions(commercialId: string | undefined) {
  return queryOptions({
    queryKey: commercialsKeys.polling(commercialId),
    queryFn: commercialId
      ? () => commercialsApi.status(commercialId).then((r) => r.data.status)
      : skipToken,
    refetchInterval: 5000,
  })
}

export type GenerationStatusSets = Record<string, GenerationStatus | undefined>

export function useCommercialsStatuses(data: Commercial[]): GenerationStatusSets {
  const results = useQueries({
    queries: data.map((commercial) => commercialStatusOptions(commercial.id)),
  })

  const resultsKey = results.map((r) => r.data).join(',')
  return useMemo<GenerationStatusSets>(() => {
    const statuses: GenerationStatusSets = {}
    data.forEach((commercial, index) => {
      statuses[commercial.id] = results[index]?.data
    })
    return statuses
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsKey])
}
